import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { AdminVideo } from '../../../common/model/dto/video/admin-video';
import { VideoUploadItem } from '../../../context/video-upload.types';
import { UploadJobPhase } from '../../../common/model/type/upload-job-phase.type';
import { uploadPercent } from '../../../context/video-upload-queue';
import OvCompactButton from '../atoms/OvCompactButton';
import OvPillLink from '../atoms/OvPillLink';
import OvVideoLink from '../atoms/OvVideoLink';
import OvVideoUploadProgress from '../atoms/OvVideoUploadProgress';
import OvVideoProcessingRetry from './OvVideoProcessingRetry';
import OvVideoStateNotice from './OvVideoStateNotice';
import { useVideoDetailsPath } from '../../../hooks/use-video-details-path';
import { isArchived } from '../../../common/utils/video-status-utils';
import Colours from '../../../design-system/colours';
import Variables from '../../../design-system/variables';

interface OvVideoUploadTrayRowProps {
  upload: VideoUploadItem;
  video: AdminVideo;
  phase: UploadJobPhase;
  canRetryUpload: boolean;
  canRetryProcessing: boolean;
  canDelete: boolean;
  onRetryUpload: (videoId: string) => void;
  onDelete: (video: AdminVideo) => void;
}

const OvVideoUploadTrayRow: FC<OvVideoUploadTrayRowProps> = ({
  upload,
  video,
  phase,
  canRetryUpload: mayRetryUpload,
  canRetryProcessing: mayRetryProcessing,
  canDelete: mayDelete,
  onRetryUpload,
  onDelete,
}) => {
  const { t } = useTranslation();
  const isLive = !isArchived(video);
  const canRetryUpload = mayRetryUpload && isLive;
  const canRetryProcessing = mayRetryProcessing && isLive;
  const canDelete = mayDelete && isLive;
  const getDetailsPath = useVideoDetailsPath();
  const progressLabel = t('videos.uploads.progressLabel', {
    title: video.title,
  });

  const renderPhase = () => {
    switch (phase) {
      case 'queued':
        return (
          <>
            <OvVideoUploadProgress label={progressLabel} percent={0} />
            <Status>{t('videos.uploads.queued')}</Status>
          </>
        );

      case 'uploading': {
        const percent = uploadPercent(upload);

        return (
          <>
            <OvVideoUploadProgress label={progressLabel} percent={percent} />
            <Status>{t('videos.uploads.uploading', { percent })}</Status>
          </>
        );
      }

      case 'finishing':
        return (
          <>
            <OvVideoUploadProgress label={progressLabel} />
            <Status>{t('videos.uploads.completing')}</Status>
          </>
        );

      case 'uploadFailed':
        return (
          <>
            <Failure>
              {t('videos.notices.uploadFailed')}
              {upload.error && <Detail>{upload.error}</Detail>}
            </Failure>
            {(canRetryUpload || canDelete) && (
              <Actions>
                {canRetryUpload && (
                  <OvCompactButton
                    tone="secondary"
                    onClick={() => onRetryUpload(upload.videoId)}
                  >
                    {t('common.actions.retry')}
                  </OvCompactButton>
                )}
                {canDelete && (
                  <OvCompactButton
                    tone="danger"
                    onClick={() => onDelete(video)}
                  >
                    {t('common.actions.delete')}
                  </OvCompactButton>
                )}
              </Actions>
            )}
          </>
        );

      case 'processing':
        return <Status>{t('videos.uploads.processing')}</Status>;

      case 'ready':
        return (
          <Actions>
            <Status>{t('videos.status.ready')}</Status>
            <OvPillLink to={getDetailsPath(video.id)}>
              {t('videos.uploads.openVideo')}
            </OvPillLink>
          </Actions>
        );

      case 'processingFailed':
        return (
          <>
            <Failure>{t('videos.uploads.processingFailed')}</Failure>
            <OvVideoStateNotice
              notice={{ kind: 'transcodeFailed', reason: video.failure_reason }}
            >
              {canRetryProcessing && (
                <OvVideoProcessingRetry videoId={video.id} />
              )}
            </OvVideoStateNotice>
          </>
        );

      default: {
        const unhandledPhase: never = phase;

        return unhandledPhase;
      }
    }
  };

  return (
    <Row>
      <Title>
        <OvVideoLink videoId={video.id}>{video.title}</OvVideoLink>
      </Title>
      {renderPhase()}
    </Row>
  );
};

export default OvVideoUploadTrayRow;

const Row = styled.li`
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  padding: ${Variables.spacing.SMALL} ${Variables.spacing.MEDIUM};
  border-bottom: 1px solid ${Colours.OV_BORDER_COLOR_V3};

  &:last-child {
    border-bottom: 0;
  }
`;

const Title = styled.span`
  font-size: ${Variables.fontSizes.MEDIUM};
  overflow-wrap: anywhere;
`;

const Status = styled.span`
  font-size: ${Variables.fontSizes.SMALL};
  color: ${Colours.OV_CHARCOAL};
`;

const Failure = styled.span`
  display: flex;
  flex-direction: column;
  font-size: ${Variables.fontSizes.SMALL};
  font-weight: bold;
  color: ${Colours.OV_ERROR_ACCESSIBLE};
`;

const Detail = styled.span`
  font-weight: normal;
  overflow-wrap: anywhere;
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
`;
